import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, Building2, Globe2, Eye, EyeOff, ArrowRight, Ban } from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useFeatureFlag } from "@/context/FeatureFlagsContext";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { isEnabled: regEnabled, loaded } = useFeatureFlag("registration");

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    organization: "",
    country: "",
  });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (loaded && !regEnabled) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4" data-testid="registration-closed">
        <div className="w-full max-w-md rounded-lg border bg-card p-8 text-center shadow-sm">
          <Ban className="mx-auto h-12 w-12 text-red-700" />
          <h1 className="mt-4 text-2xl font-bold">Inscriptions fermées</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Les inscriptions sont temporairement désactivées par l'administrateur.
            Merci de revenir plus tard.
          </p>
          <Link to="/" className="mt-6 inline-block"><Button variant="outline">Retour à l'accueil</Button></Link>
        </div>
      </div>
    );
  }

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.password) {
      setError("Nom, email et mot de passe sont obligatoires.");
      return;
    }
    if (form.password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    setLoading(true);
    const res = await register({
      ...form,
      email: form.email.trim().toLowerCase(),
      name: form.name.trim(),
      organization: form.organization.trim() || null,
      country: form.country.trim() || null,
    });
    setLoading(false);
    if (res.ok) {
      toast.success("Compte créé avec succès. Bienvenue.");
      navigate("/app/dashboard", { replace: true });
    } else {
      setError(res.error);
      toast.error(res.error);
    }
  };

  return (
    <div className="relative min-h-screen bg-background text-foreground" data-testid="register-page">
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-[0.2]" />
      <div className="pointer-events-none absolute -right-24 top-24 h-80 w-80 rounded-full bg-brand-gold/10 blur-3xl" />

      <header className="relative z-10 flex items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-3" data-testid="register-home-link">
          <Logo size={36} />
          <div className="leading-tight">
            <div className="text-base font-bold">
              VITAE-PUBLICA<span className="text-[0.6em] align-top">™</span>
            </div>
            <div className="text-[11px] italic text-muted-foreground">
              Les Finances Publiques sont des Tranches de Vies
            </div>
          </div>
        </Link>
        <ThemeToggle />
      </header>

      <div className="relative z-10 mx-auto flex w-full max-w-lg flex-col px-6 pt-6 pb-16 animate-fade-in-up">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-extrabold tracking-tight">Créer un compte</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Rejoignez la communauté d'audit vitaliste des finances publiques.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="register-form"
          className="rounded-xl border border-border bg-card p-6 shadow-sm"
        >
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Nom complet *</Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="name"
                  value={form.name}
                  onChange={onChange("name")}
                  className="pl-9"
                  placeholder="Dr. Aïcha Diallo"
                  data-testid="register-name-input"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={onChange("email")}
                  className="pl-9"
                  placeholder="vous@institution.org"
                  data-testid="register-email-input"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Mot de passe * (≥ 8 caractères)</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="new-password"
                  value={form.password}
                  onChange={onChange("password")}
                  className="pl-9 pr-10"
                  placeholder="••••••••"
                  data-testid="register-password-input"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  aria-label={showPwd ? "Masquer" : "Afficher"}
                  data-testid="register-toggle-pwd"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="organization">Organisation</Label>
                <div className="relative mt-1.5">
                  <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="organization"
                    value={form.organization}
                    onChange={onChange("organization")}
                    className="pl-9"
                    placeholder="Université / ONG…"
                    data-testid="register-organization-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="country">Pays</Label>
                <div className="relative mt-1.5">
                  <Globe2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="country"
                    value={form.country}
                    onChange={onChange("country")}
                    className="pl-9"
                    placeholder="Sénégal"
                    data-testid="register-country-input"
                  />
                </div>
              </div>
            </div>

            {error && (
              <div
                className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                data-testid="register-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 text-base font-semibold"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? "Création du compte…" : "Créer mon compte"}
              {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
          </div>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Déjà inscrit ?{" "}
          <Link
            to="/login"
            className="font-semibold text-foreground underline-offset-4 hover:underline"
            data-testid="register-goto-login"
          >
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
}
